/**
 * The consumer page's brand token vocabulary — `page_templates.tokens`, typed.
 *
 * Spec §12.1 Rail 2 (the theming boundary as a liability shield), §15.12 (tokens only,
 * permanently — `custom_css` does not exist and must not be reintroduced), and the design
 * handoff's `brand-tokens.md`. Phase 3 plan §5.3 is the argued mechanism; §6 is the scope.
 *
 * ## This is a liability document that happens to compile
 *
 * `brand-tokens.md`'s own framing, and it is the reason for the citation discipline below:
 * *"a hosted renderer that lets each tenant restyle safety content into illegibility has no
 * defensible standard of care."* Every bound in this file is therefore either **read off a
 * design artifact and cited to the line**, or **derived from one that is** — and where it is
 * neither, it says so in as many words. A floor invented here is a liability claim invented
 * here, and the sentence a retailer's QA is pointed at is only as good as the weakest number
 * behind it.
 *
 * ## Recovered scope, named rather than absorbed
 *
 * The Phase 3 plan's U3 file list named `packages/shared/src/` *(template token schema +
 * clamps, domain enums)*. Only the enums landed — `serving.ts` has `DOMAIN_KINDS`,
 * `DOMAIN_STATUSES` and `PAGE_TEMPLATE_LAYOUTS`, and `page_templates.tokens` has been
 * unvalidated `jsonb` since `0016`. c81 recovers the other half because it is the first
 * commit that renders a brand token and **cannot clamp against a floor that does not
 * exist**. Recorded in the Drift Log as a dropped half, not as new scope.
 *
 * ## Two mechanisms that fail differently, and why the read path deliberately does not clamp
 *
 * §5.3: *"Floors are enforced at both ends. The write path clamps … the renderer **also**
 * wraps every brand-scaled value in `clamp()` … because the write path alone cannot defend
 * against a row written around the API, and right now there **is** no API."*
 *
 * Written naively that is one mechanism twice: if {@link brandTokensSchema} clamped on read,
 * a hostile row would arrive at the stylesheet already corrected and the CSS `clamp()` would
 * never fire — so the two halves could not be observed apart, and c80's lesson is exactly
 * that (*"beware assertions that pass because the seed agrees"*). So the split is deliberate
 * and is the whole design:
 *
 *   - {@link brandTokensSchema} validates **shape** and nothing else. `apps/pip` parses with
 *     it and emits the number it got.
 *   - {@link brandTokensWriteSchema} is that schema plus {@link clampBrandScales}. It is what
 *     a write path parses with, and it is why the API unit needs no clamp of its own.
 *   - The **stylesheet** wraps every consumption in CSS `clamp()` over these same two
 *     constants — a bound applied by the browser's cascade at paint time, which no
 *     TypeScript defeat can reach and which survives a value that never passed through this
 *     file at all.
 *
 * The two therefore fail in different processes for different reasons, and the seeded
 * hostile fixture — written **below** the floor, around the API that does not exist — makes
 * both separately visible: the served stylesheet still carries the hostile number, and the
 * painted text still measures the floor.
 */
import { z } from "zod";
import { PAGE_TEMPLATE_LAYOUTS } from "./serving.js";
/**
 * The shape's version, carried in the row so Phase 9 is a migration of values rather than a
 * guess about what a stored object meant.
 *
 * `page_templates.tokens` is `jsonb` with no schema behind it in the database (0016's own
 * comment says so), so the only thing that can date a stored object is a field inside it.
 * Absent means version 1: every row written before this constant existed — which today is
 * every row — was written against this vocabulary.
 */
export const BRAND_TOKEN_SCHEMA_VERSION = 1;
/**
 * The base tier — **nine tokens, where `brand-tokens.md` says eight.**
 *
 * Its own summary line reads *"Base tier (8): `wordmark`, `legal-name`, `header-bg`,
 * `header-fg`, `accent`, `pairing`, `layout`, `section-order` + `optional-sections`"* — nine
 * names under the number eight, and the table above it marks nine rows `base`. This constant
 * is the parser-countable number and the table is what it counts, for the same reason
 * `PAGE_TEMPLATE_LAYOUTS` is three where `screens.md` says four: **a test written to the
 * prose would be red against correct work.**
 *
 * The eighth self-miscount in that package, and recorded in the Drift Log with the others.
 */
export const BASE_BRAND_TOKENS = [
    "wordmark",
    "legal-name",
    "header-bg",
    "header-fg",
    "accent",
    "pairing",
    "layout",
    "section-order",
    "optional-sections",
];
/**
 * The other thirty-two — Scale tier, Phase 9, **declared by name and not yet validated**.
 *
 * This is what "the extended-41 slots reserved so Phase 9 is values rather than surgery"
 * means concretely: the *list* is settled now, from `brand-tokens.md`'s table read top to
 * bottom, so Phase 9 adds validators to known names rather than rediscovering the vocabulary
 * from a mock. Two of them — `type-scale` and `space-scale` — are validated **today** anyway,
 * because their floors are what the boundary test is about; see {@link brandTokensSchema}.
 *
 * Order is the table's order (Identity · Colour · Type · Form · Art direction · Structure),
 * so the two can be diffed by eye.
 */
export const EXTENDED_BRAND_TOKENS = [
    // Identity · the two the base tier does not get
    "wordmark-height",
    "favicon",
    // Colour · six of the nine
    "page-bg",
    "section-bg-alt",
    "surface",
    "rule",
    "secondary-1",
    "secondary-2",
    // Type · six of the seven
    "heading-weight",
    "heading-tracking",
    "heading-case",
    "type-scale",
    "label-case",
    "label-tracking",
    // Form · all eight
    "space-scale",
    "radius-sm",
    "radius-lg",
    "rule-weight",
    "button-shape",
    "tile-style",
    "tile-divider",
    "chevron-style",
    // Art direction · all six
    "hero-crop",
    "hero-ratio",
    "hero-overlay",
    "hero-mark-position",
    "image-treatment",
    "image-radius",
    // Structure · four of the seven
    "layout-override",
    "section-bg-map",
    "preset",
    "preset-scope",
];
/**
 * All forty-one, which is the number `brand-tokens.md`'s title states and the number its
 * table actually contains — the two agree here even though the base split does not.
 *
 * Asserted as a length rather than trusted: `9 + 32 = 41` is the one arithmetic claim in
 * this file, and the test that checks it is checking the design's total against ours.
 */
export const BRAND_TOKENS = [...BASE_BRAND_TOKENS, ...EXTENDED_BRAND_TOKENS];
/** The count the handoff's title claims. A named constant so a test can state it. */
export const BRAND_TOKEN_COUNT = 41;
/**
 * `type-scale`'s enforced floor.
 *
 * **Cited, and not from where the kickoff said to look.** `brand-tokens.md` marks the token
 * *"number · **floor enforced**"* (its Type group) and says floors exist (*"`type-scale` and
 * `space-scale` carry an enforced floor: they scale the brand's own content and cannot
 * reduce a protected block below its fixed presentation"*) — but it states **no number**, and
 * neither does any other prose in the handoff or the vault.
 *
 * The number is in the design reference, `designs/Screen · Consumer Page.dc.html` §07 — *"The
 * boundary test, run with a hostile token set · **Every Scale token pushed to its worst legal
 * value**"* — over the literal block `type-scale: 0.8 · space-scale: 0.6`, and corroborated
 * by that section's own prose (*"0.8 type scale"* as the hostile minimum the design
 * demonstrates the protected blocks surviving).
 *
 * *"Worst legal value"* is a normative sentence: it asserts 0.8 is legal and that nothing
 * legal is worse. That is the definition of a floor, so reading it off is a citation rather
 * than an invention — but the artifact the kickoff named does not carry it, and
 * `brand-tokens.md` is owed the validated ranges as normative text. Drift Log 🟡.
 */
export const TYPE_SCALE_FLOOR = 0.8;
/** `space-scale`'s enforced floor. Same citation, same line: `space-scale: 0.6`. */
export const SPACE_SCALE_FLOOR = 0.6;
/**
 * The ceilings — **an engineering bound, and explicitly not a design ruling.**
 *
 * The floor is the liability-bearing end and is cited. **No artifact states an upper bound
 * for either scale**, and one is needed for two mechanical reasons rather than any safety
 * reason: zod needs a finite range, and a CSS `clamp()` needs a well-formed third argument.
 * These two numbers are therefore ours, they are interim, and design owes the range table —
 * filed to Cameron as a rubber-stamp question beside the floors themselves.
 *
 * **The ceiling is liability-neutral under §12.1's letter, and the argument is worth keeping
 * rather than asserting:**
 *
 *   1. A recall cannot be reached by any scale value at all. §07: *"Recall mode additionally
 *      discards the token set entirely, so it cannot be reached by any combination."* That
 *      is foreclosed by suppression (c82), not by a bound.
 *   2. The allergen block's protected properties are its **own metrics and its order** —
 *      *"position above marketing copy, 7:1 contrast, 16px floor"* (§02's protected panel) —
 *      and §12.1 forbids *reducing legibility*, *hiding the banner*, and *removing* the
 *      attribution or provenance. All three are invariant under any spacing multiplier:
 *      "below the fold" is not a property §12.1 names, and DOM order survives every scale.
 *      The design's own classic layout already puts a 200px photo above the allergen block,
 *      so "above the fold at 390" was never the guarantee.
 *   3. What a ceiling *does* foreclose is the absurd end — a `space-scale` of 10 is
 *      screenfuls of nothing — which is what an engineering bound is for and all it is for.
 *
 * **Argued is not evidenced, so the ceiling gets a fixture.** Once an upper bound exists,
 * *"worst legal value"* has two ends, and c85's hostile token set owes a **ceiling-end
 * variant** asserting the allergen block still precedes marketing copy in DOM order. Named
 * here because that is the assertion this reasoning is a promissory note for.
 */
export const TYPE_SCALE_CEILING = 1.5;
/** See {@link TYPE_SCALE_CEILING}. Interim, ours, and owed a design ruling. */
export const SPACE_SCALE_CEILING = 2.0;
/**
 * The font pairings — **three members, and only one of them has faces in the tree.**
 *
 * §6 ships *"the two fully-drawn pairings (Lora + Work Sans, Source Serif 4 + Libre Franklin)
 * plus the platform default (Archivo) — three members, all self-hosted OFL, vendored per-face
 * with lock rows."* The enum is that ruling. **The vendoring is not**: c80 vendored Archivo
 * and IBM Plex Mono alone, U5's and U6's file lists named no other font work, and four
 * families' worth of subsetting, OFL texts and lock rows is a unit of its own (c84a, 0.25 ew,
 * proposed — Drift Log).
 *
 * So until c84a lands, a member with no vendored face **resolves to the Archivo stack, never
 * to a bare `font-family` with no `@font-face` behind it**. A page in the wrong *chosen* face
 * but the right *platform* face is honestly degraded — Archivo is vendored, metric-matched
 * and self-hosted, and the CLS criterion is written against it. A page in whatever the
 * operating system happens to have is neither, and it would arrive silently. `apps/pip`'s
 * `lib/tokens.ts` owns that mapping, marks the two rows `vendored: false`, and a test asserts
 * **no unvendored family name ever reaches served CSS** — the assertion c84a inverts when it
 * flips the flags.
 *
 * The exposure is near-zero meanwhile: the only writer of `page_templates.tokens` in this
 * phase is the seed, and it writes `archivo`.
 *
 * `brand-tokens.md` says the picker offers **nine** pairings and enumerates four; §2 of the
 * plan records that gap as design's to close (Q-S4). Shipping nine invented pairings would be
 * a number nobody read off a source.
 */
export const BRAND_PAIRINGS = [
    /** The platform face. The default, and the only one with vendored files before c84a. */
    "archivo",
    /** *"Warm, editorial. Good for food, wine, and long ingredient lists."* — §03's picker. */
    "lora-work-sans",
    /** *"Neutral and institutional. The default for the compliance layout."* — §03's picker. */
    "source-serif-libre-franklin",
];
/** The pairing a template that names none renders in. */
export const DEFAULT_BRAND_PAIRING = "archivo";
/**
 * The sections a brand may reorder, in their default order.
 *
 * `section-order` is *"ordered list, **protected sections pinned**"* (`brand-tokens.md`'s
 * Structure group). The pinning is the whole of the token's safety story, and it is expressed
 * here by **omission rather than by a rule**: the protected blocks are simply not members of
 * this vocabulary, so no permutation of it can move one. `screens.md` §12's protected list —
 * allergen block, safety-information block, batch/expiry format, nutrition semantics and
 * order, language switcher, provenance line, footer privacy line, attribution — names every
 * section that is absent from this array, and that correspondence is the thing to check when
 * a section is added.
 *
 * **This vocabulary is c81's and grows with the layouts.** It names what the classic layout
 * renders; hero and compliance (c84) add theirs, and compliance additionally *removes*
 * marketing members structurally rather than by ordering. Named as an invention here because
 * no artifact enumerates the section keys — the *rule* is cited, the *names* are ours.
 */
export const ORDERABLE_PIP_SECTIONS = [
    /** The product gallery. Renders only where media is servable — see `apps/pip`'s `mediaUrl`. */
    "gallery",
    /** `products.description`, the brand's own prose. */
    "description",
    /** `products.attributes`, through the `attribute_defs` registry — the "more information" rows. */
    "attributes",
    /** Third-party media as link-out cards. Never embeds (§12.1 Rail 1). */
    "links",
];
/**
 * A CSS hex colour, and **the only colour syntax a token may carry.**
 *
 * Three and six digits, `#` required, no `rgb()`, no `color()`, no named colours, no alpha.
 * That is narrower than CSS and deliberately so: this value is serialised into a stylesheet,
 * and a permissive colour grammar is a place where a tenant string reaches a CSS parser.
 * Every value that leaves this schema can be reconstructed from its digits, which is what
 * makes the emitter's *"no tenant string ever reaches the CSS"* property true by construction
 * rather than by escaping.
 *
 * Eight-digit `#rrggbbaa` is refused for a second reason: a brand cannot be permitted to make
 * its own header transparent and reveal whatever the platform paints beneath it.
 */
const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const hexColour = (token) => z.string().regex(HEX_COLOUR, `${token} must be a hex colour like #3b2b22 or #fff.`);
/**
 * One template's token set.
 *
 * **Every field is optional**, because a partially-themed template is the normal case: a
 * brand that has set a header colour and nothing else is a brand mid-onboarding, not an
 * invalid row. The renderer's job is to have a platform answer for every slot, which is what
 * `tokens-brand.css`'s fallbacks are.
 *
 * **Unknown keys are stripped, not refused** (zod's default, stated because it is load-
 * bearing here). The thirty reserved names in {@link EXTENDED_BRAND_TOKENS} that this schema
 * does not yet validate are therefore *accepted into the row and dropped on read*, which is
 * the behaviour Phase 9 wants: a Scale-tier value written early survives in `jsonb` and
 * starts rendering the day its validator lands, rather than being destroyed by a read.
 *
 * **This schema does not clamp.** See the module header — that is {@link brandTokensWriteSchema}'s
 * job, and keeping them apart is what lets the two floor mechanisms be observed separately.
 */
export const brandTokensSchema = z
    .object({
    /**
     * The schema version this object was written against. See {@link BRAND_TOKEN_SCHEMA_VERSION};
     * absent means 1, which is every row written so far.
     */
    version: z.literal(BRAND_TOKEN_SCHEMA_VERSION).optional(),
    // ---- Identity ----------------------------------------------------------
    /**
     * An object-storage key for the brand's wordmark — **never a URL** (`brand-tokens.md`:
     * *"No token accepts a URL"*, which is what forecloses `@import`/`url()` as an external
     * fetch vector). Turning a key into a servable address is the renderer's job, and on
     * this rail there is nothing serving media bytes yet — see `apps/pip`'s `mediaUrl`.
     */
    wordmark: z.string().min(1).max(512).optional(),
    /**
     * The brand's legal name — *"string — feeds the provenance line"*.
     *
     * The provenance line is protected markup and this is the one tenant-authored value
     * inside it, which is the point rather than a hole: §12.1's fixed sentence is *"Product
     * information provided by {brand legal name}"*, and attributing responsibility to the
     * brand is what makes our fixed rendering of their safety content defensible. Rendered
     * with `data-verbatim-source`, like every other tenant-authored string.
     */
    "legal-name": z.string().min(1).max(200).optional(),
    // ---- Colour ------------------------------------------------------------
    "header-bg": hexColour("header-bg").optional(),
    "header-fg": hexColour("header-fg").optional(),
    /**
     * The brand's accent. Reaches links, rules and emphasis **in the brand's own content
     * only** — never a protected block, which is a property of `protected.css` rather than
     * of this field.
     */
    accent: hexColour("accent").optional(),
    // ---- Type --------------------------------------------------------------
    pairing: z.enum(BRAND_PAIRINGS).optional(),
    /**
     * The brand's type multiplier. Floored at {@link TYPE_SCALE_FLOOR}.
     *
     * Scale-tier by `brand-tokens.md`'s table and validated here anyway, because the floor is
     * what the boundary test is about and a floor with no validated field behind it is a
     * sentence rather than a mechanism. Phase 9 gates it by tier; nothing gates it today,
     * which is the same posture `/audit-log` has.
     */
    "type-scale": z
        .number("type-scale must be a number.")
        .positive("type-scale must be greater than zero.")
        .finite("type-scale must be a finite number.")
        .optional(),
    // ---- Form --------------------------------------------------------------
    /** The brand's spacing multiplier. Floored at {@link SPACE_SCALE_FLOOR}. See above. */
    "space-scale": z
        .number("space-scale must be a number.")
        .positive("space-scale must be greater than zero.")
        .finite("space-scale must be a finite number.")
        .optional(),
    // ---- Structure ---------------------------------------------------------
    layout: z.enum(PAGE_TEMPLATE_LAYOUTS).optional(),
    /**
     * A permutation of {@link ORDERABLE_PIP_SECTIONS}.
     *
     * Validated as a **set** rather than as a prefix: a partial list would leave the renderer
     * deciding where the unnamed sections go, which is a second ordering rule in a second
     * place. Duplicates are refused for the same reason — a section rendered twice is a
     * question this vocabulary should not be able to ask.
     */
    "section-order": z
        .array(z.enum(ORDERABLE_PIP_SECTIONS))
        .refine((order) => new Set(order).size === order.length, "section-order may not name a section twice.")
        .refine((order) => order.length === ORDERABLE_PIP_SECTIONS.length, `section-order must name all ${String(ORDERABLE_PIP_SECTIONS.length)} orderable sections.`)
        .optional(),
    /**
     * Which orderable sections render at all — *"boolean set"*.
     *
     * Absent means every section renders; a section absent from the map renders. Only an
     * explicit `false` suppresses one, so the failure mode of a partially-written map is a
     * page with too much on it rather than a page missing its ingredients.
     */
    "optional-sections": z
        .partialRecord(z.enum(ORDERABLE_PIP_SECTIONS), z.boolean())
        .optional(),
})
    .describe("page_templates.tokens — the base tier, plus the two floored scale tokens.");
/**
 * Bring the two scale tokens inside their validated ranges.
 *
 * The **write path's** half of the two mechanisms, and the one that produces a warning rather
 * than a refusal: §12.1 is explicit that brand-side self-harm is *"warn-only … and the warn
 * lives in the write path — never a block."* A tenant who asks for 0.5 gets 0.8 and a page
 * that works, which is the outcome the ruling wants; what they must never get is a refusal
 * that reads as us policing their design.
 *
 * Exported on its own as well as through {@link brandTokensWriteSchema} so that the API unit
 * can tell *"was this value moved?"* — a caller comparing input to output learns whether to
 * warn, without this function needing to know what a warning is.
 */
export function clampBrandScales(tokens) {
    const clamped = { ...tokens };
    if (clamped["type-scale"] !== undefined) {
        clamped["type-scale"] = Math.min(Math.max(clamped["type-scale"], TYPE_SCALE_FLOOR), TYPE_SCALE_CEILING);
    }
    if (clamped["space-scale"] !== undefined) {
        clamped["space-scale"] = Math.min(Math.max(clamped["space-scale"], SPACE_SCALE_FLOOR), SPACE_SCALE_CEILING);
    }
    return clamped;
}
/**
 * What a **write path** parses `page_templates.tokens` with.
 *
 * Nothing calls it yet — the `/page-templates` API is scoped into this phase by §6 and D13
 * and assigned to no unit (Drift Log; proposed as its own unit rather than smuggled here).
 * It exists now because the alternative is that the first writer invents its own clamping,
 * and *"two mechanisms that fail differently"* becomes two spellings that fail the same way.
 */
export const brandTokensWriteSchema = brandTokensSchema.transform(clampBrandScales);
//# sourceMappingURL=brand-tokens.js.map